import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, RotateCw, Sparkles } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { apiErrorMessage } from "@/lib/api";
import { bannerDownloadUrl, listBanners, retryBanner, type BannerDto } from "@/api/banners";

interface Props {
    pageId: number;
}

/**
 * Read-mostly summary of every banner attached to this page. Generation +
 * editing happens in the constructor (Banners tab); this surface is for
 * browsing + downloading after the fact.
 */
export function BannersSection({ pageId }: Props): React.ReactNode {
    const bannersQuery = useQuery({
        queryKey: ["banners", "page", pageId],
        queryFn: () => listBanners(pageId),
    });

    if (bannersQuery.isLoading) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Banners</CardTitle>
                </CardHeader>
                <CardContent className="flex items-center justify-center py-12">
                    <Spinner size="lg" />
                </CardContent>
            </Card>
        );
    }
    const banners = bannersQuery.data || [];

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Banners</CardTitle>
                <CardDescription>
                    Paid-media creative packs attached to this page. Built + edited in the AI Constructor.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {banners.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted-foreground">
                        <Sparkles className="h-6 w-6 text-muted-foreground/60" />
                        <div>
                            No banners yet. Open this page in the constructor and ask the agent to
                            build a banner pack.
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {groupByPlatform(banners).map(([platform, list]) => (
                            <div key={platform}>
                                <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{platform}</div>
                                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                                    {list.map((b) => (
                                        <div
                                            key={b.id}
                                            className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3"
                                            data-testid={`page-banner-${b.id}`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <div className="truncate text-sm font-medium" title={b.name}>{b.name}</div>
                                                    <div className="text-[10px] text-muted-foreground">
                                                        {b.status} · {b.assetCount} asset{b.assetCount === 1 ? "" : "s"}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <BannerRetryButton banner={b} pageId={pageId} />
                                                    <a
                                                        href={bannerDownloadUrl(b.id)}
                                                        download
                                                        className="inline-flex items-center gap-1 rounded p-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                                                        title="Download zip"
                                                    >
                                                        <Download className="h-3.5 w-3.5" />
                                                    </a>
                                                </div>
                                            </div>
                                            {(b.assets || []).filter((a) => a.kind === "image" || a.kind === "logo").length > 0 && (
                                                <div className="grid grid-cols-4 gap-1">
                                                    {(b.assets || [])
                                                        .filter((a) => a.kind === "image" || a.kind === "logo")
                                                        .slice(0, 4)
                                                        .map((a) => (
                                                            <div key={a.id} className="aspect-square overflow-hidden rounded bg-muted/40">
                                                                <img src={a.url ?? ""} alt="" className="h-full w-full object-cover" />
                                                            </div>
                                                        ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

/** One-click retry for a failed or wedged banner — shown only when applicable. */
function BannerRetryButton({ banner, pageId }: { banner: BannerDto; pageId: number }): React.ReactNode {
    const queryClient = useQueryClient();
    const retryMut = useMutation({
        mutationFn: () => retryBanner(banner.id),
        onSuccess: () => {
            toast.success("Retrying — re-checking this banner");
            queryClient.invalidateQueries({ queryKey: ["banners", "page", pageId] });
        },
        onError: (e) => toast.error(apiErrorMessage(e, "Retry failed")),
    });
    if (banner.status !== "failed" && banner.status !== "generating" && banner.status !== "validating") {
        return null;
    }
    return (
        <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => retryMut.mutate()}
            disabled={retryMut.isPending}
            title="Re-check this banner. Recovers a failed one whose assets are fine, or un-sticks one wedged mid-generation."
            data-testid={`page-banner-retry-${banner.id}`}
        >
            {retryMut.isPending ? <Spinner size="sm" /> : <RotateCw className="h-3.5 w-3.5" />}
            Retry
        </Button>
    );
}

function groupByPlatform(banners: BannerDto[]): [string, BannerDto[]][] {
    const m = new Map<string, BannerDto[]>();
    for (const b of banners) {
        const arr = m.get(b.platform) || [];
        arr.push(b);
        m.set(b.platform, arr);
    }
    return [...m.entries()];
}
